/* Kripson Home theme script. No third-party libraries or trackers. */
(() => {
  'use strict';
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const KH = window.KH || { routes: {} };

  /* ---------- helpers ---------- */
  const money = (cents) => {
    const fmt = KH.moneyFormat || 'Rs {{amount}}';
    const n = Number(cents) / 100;
    const whole = Number.isInteger(n);
    const withDelims = (num, decimals) => num.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return withDelims(n, 0);
      if (key === 'amount_with_comma_separator') return n.toFixed(2).replace('.', ',');
      return withDelims(n, whole ? 0 : 2);
    });
  };
  const toast = (msg) => {
    const t = $('[data-toast]');
    if (!t) return;
    t.textContent = msg;
    t.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(() => { t.hidden = true; }, 3200);
  };
  const focusables = (root) => $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', root)
    .filter((el) => el.offsetParent !== null);

  /* Accessible modal helper: opens, traps focus, closes on Esc, restores focus. */
  const modal = (target, { onClose } = {}) => {
    const node = () => (typeof target === 'function' ? target() : target);
    let lastFocus = null;
    const keyHandler = (e) => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      const f = focusables(node());
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    const open = (opener) => {
      lastFocus = opener || document.activeElement;
      node().classList.add('is-open');
      document.body.style.overflow = 'hidden';
      document.removeEventListener('keydown', keyHandler);
      document.addEventListener('keydown', keyHandler);
      setTimeout(() => { const f = focusables(node()); (f.find((x) => x.matches('input[type="search"], input[type="email"]')) || f[0])?.focus(); }, 60);
    };
    const close = () => {
      node().classList.remove('is-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', keyHandler);
      lastFocus?.focus?.();
      onClose?.();
    };
    return { open, close, get isOpen() { return node().classList.contains('is-open'); } };
  };

  /* ---------- preloader → KH.ready ---------- */
  const preloader = $('[data-preloader]');
  KH.ready = new Promise((resolve) => {
    if (!preloader || document.documentElement.classList.contains('no-preloader')) { preloader?.remove(); resolve(); return; }
    const started = performance.now();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      const wait = Math.max(0, 900 - (performance.now() - started));
      setTimeout(() => {
        preloader.classList.add('is-done');
        setTimeout(resolve, 380); // start entry animations while the panel is lifting
        setTimeout(() => preloader.remove(), 1000);
      }, wait);
    };
    if (document.readyState === 'complete') finish();
    else window.addEventListener('load', finish, { once: true });
    setTimeout(finish, 2200); // never hold the visitor longer than this
  });

  /* ---------- skeleton shimmer until images load ---------- */
  const skTargets = '.card__media, .gallery__slide, .gallery__thumb, .stack__img, .use-card, .wholesale__media, .line__img, .quote-line, .predictive__item a';
  const watchImages = (root = document) => {
    $$(skTargets, root).forEach((box) => {
      const img = $('img', box);
      if (!img || box.dataset.sk) return;
      box.dataset.sk = '1';
      box.classList.add('sk-img');
      if (img.complete && img.naturalWidth) return;
      box.classList.add('sk-wait');
      const done = () => box.classList.remove('sk-wait');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  };
  watchImages();
  KH.watchImages = watchImages;

  /* ---------- header ---------- */
  const header = $('[data-header]');
  if (header) {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      const overlayOpen = document.body.style.overflow === 'hidden';
      if (!overlayOpen) header.classList.toggle('is-hidden', y > 320 && y > lastY + 4);
      if (y < lastY - 4 || y < 320) header.classList.remove('is-hidden');
      lastY = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    header.addEventListener('focusin', () => header.classList.remove('is-hidden'));
  }

  const navEl = $('[data-mobile-nav]');
  if (navEl) {
    const nav = modal(navEl, { onClose: () => $$('[data-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'false')) });
    $$('[data-menu-open]').forEach((b) => b.addEventListener('click', () => { b.setAttribute('aria-expanded', 'true'); nav.open(b); }));
    $$('[data-menu-close]', navEl).forEach((b) => b.addEventListener('click', nav.close));
  }

  /* ---------- search + predictive ---------- */
  const searchEl = $('[data-search]');
  if (searchEl) {
    const s = modal(searchEl, { onClose: () => $$('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false')) });
    $$('[data-search-open]').forEach((b) => b.addEventListener('click', () => { b.setAttribute('aria-expanded', 'true'); s.open(b); }));
    $$('[data-search-close]', searchEl).forEach((b) => b.addEventListener('click', s.close));
    searchEl.addEventListener('click', (e) => { if (e.target === searchEl) s.close(); });
    const input = $('[data-search-input]', searchEl);
    const out = $('[data-predictive]', searchEl);
    let timer, ctrl;
    input?.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ''; return; }
      out.innerHTML = '<div aria-hidden="true">' + '<div class="sk-row"><span class="sk-line sk-box"></span><span class="sk-line"></span><span class="sk-line"></span></div>'.repeat(3) + '</div>';
      timer = setTimeout(async () => {
        ctrl?.abort(); ctrl = new AbortController();
        try {
          const url = `${KH.routes.predictiveSearch || '/search/suggest'}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,tag`;
          const res = await fetch(url, { signal: ctrl.signal });
          const data = await res.json();
          const items = data?.resources?.results?.products || [];
          if (!items.length) { out.innerHTML = `<p class="predictive__empty">No towels match “${q.replace(/[<>&"]/g, '')}”.</p>`; return; }
          out.innerHTML = `<ul class="predictive__list">${items.map((p) => `
            <li class="predictive__item"><a href="${p.url}">
              ${p.image ? `<img src="${p.image}&width=120" alt="" width="56" height="56" loading="lazy">` : '<span></span>'}
              <span><strong>${p.title}</strong><br><small class="muted">${p.type || ''}</small></span>
              <span class="price">${money(Math.round(parseFloat(p.price) * 100))}</span>
            </a></li>`).join('')}</ul>`;
          watchImages(out);
        } catch (e) { /* aborted or offline: keep quiet */ }
      }, 220);
    });
  }

  /* ---------- cart ---------- */
  const drawerSectionId = 'cart-drawer';
  const getDrawer = () => $('[data-cart-drawer]');
  const drawerModal = getDrawer() ? modal(getDrawer) : null;
  const bindDrawer = () => {
    const el = getDrawer();
    if (!el) return;
    $$('[data-cart-close]', el).forEach((b) => b.addEventListener('click', () => drawerModal.close()));
    bindLines(el);
  };
  const setCount = (n) => {
    $$('[data-cart-count]').forEach((c) => { c.textContent = n; c.dataset.count = n; });
    $$('[data-cart-count-text]').forEach((c) => { c.textContent = n; });
  };
  const renderDrawer = (html, keepOpen = true) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-drawer]');
    const old = getDrawer();
    if (!fresh || !old) return;
    const wasOpen = old.classList.contains('is-open');
    old.replaceWith(fresh);
    if (wasOpen && keepOpen) fresh.classList.add('is-open');
    setCount(fresh.dataset.count || 0);
    bindDrawer();
    watchImages(fresh);
  };
  const sectionsToRender = () => {
    const ids = [drawerSectionId];
    const page = $('[data-cart-page]')?.closest('.shopify-section');
    if (page) ids.push(page.id.replace('shopify-section-', ''));
    return ids;
  };
  const updateLine = async (line, quantity, scope) => {
    scope?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${KH.routes.cartChange}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: sectionsToRender(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (data.status) { toast(data.description || 'Could not update the cart.'); return; }
      renderDrawer(data.sections?.[drawerSectionId]);
      const pageSection = $('[data-cart-page]')?.closest('.shopify-section');
      if (pageSection && data.sections?.[pageSection.id.replace('shopify-section-', '')]) {
        const doc = new DOMParser().parseFromString(data.sections[pageSection.id.replace('shopify-section-', '')], 'text/html');
        pageSection.innerHTML = doc.querySelector('.shopify-section')?.innerHTML || doc.body.innerHTML;
        bindLines(pageSection);
      }
      toast(quantity === 0 ? 'Removed from cart' : 'Cart updated');
    } catch (e) {
      window.location.href = KH.routes.cart;
    } finally { scope?.removeAttribute('aria-busy'); }
  };
  function bindLines(root) {
    $$('[data-line]', root).forEach((row) => {
      const line = Number(row.dataset.line);
      $$('[data-line-qty]', row).forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        updateLine(line, Math.max(0, Number(b.dataset.lineQty)), row);
      }));
      const input = $('[data-line-input]', row);
      input?.addEventListener('change', () => updateLine(line, Math.max(0, parseInt(input.value, 10) || 0), row));
    });
  }
  bindDrawer();
  const pageCart = $('[data-cart-page]');
  if (pageCart) bindLines(pageCart.closest('.shopify-section') || pageCart);

  $$('[data-cart-open]').forEach((a) => a.addEventListener('click', (e) => {
    if (KH.cartType !== 'drawer' || !drawerModal || $('[data-cart-page]')) return;
    e.preventDefault();
    drawerModal.open(a);
    document.dispatchEvent(new CustomEvent("kh:cart-open"));
  }));

  const addToCart = async (form, button) => {
    const err = $('[data-form-error]', form);
    if (err) err.hidden = true;
    button?.classList.add('is-loading');
    button?.setAttribute('aria-disabled', 'true');
    try {
      const fd = new FormData(form);
      const body = { items: [{ id: Number(fd.get('id')), quantity: Math.max(1, parseInt(fd.get('quantity'), 10) || 1) }], sections: [drawerSectionId], sections_url: window.location.pathname };
      const res = await fetch(`${KH.routes.cartAdd}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.status) {
        if (err) { err.innerHTML = `<span>${data.description || data.message || 'Could not add to cart.'}</span>`; err.hidden = false; }
        else toast(data.description || 'Could not add to cart.');
        return;
      }
      if (KH.cartType === 'page') { window.location.href = KH.routes.cart; return; }
      renderDrawer(data.sections?.[drawerSectionId], false);
      drawerModal?.open(button);
      document.dispatchEvent(new CustomEvent("kh:cart-open"));
    } catch (e) {
      form.submit();
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-disabled');
    }
  };

  /* ---------- product page ---------- */
  $$('[data-product-section]').forEach((section) => {
    const json = $('[data-product-json]');
    if (!json) return;
    const { variants, media } = JSON.parse(json.textContent);
    const form = $('[data-product-form]', section);
    const idInput = $('[data-variant-id]', section);
    const addBtn = $('[data-add]', section);
    const addLabel = $('[data-add-label]', section);
    const stock = $('[data-stock]', section);
    const slides = $('[data-gallery-slides]', section);
    const thumbs = $$('[data-thumb]', section);
    const indexEl = $('[data-gallery-index]', section);
    const sticky = $('[data-sticky-buy]');

    /* gallery */
    const slideEls = slides ? $$('.gallery__slide', slides) : [];
    const goTo = (i, smooth = true) => {
      if (!slides || !slideEls[i]) return;
      slides.scrollTo({ left: slideEls[i].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    };
    const markThumb = (i) => {
      thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
      if (indexEl) indexEl.textContent = i + 1;
    };
    thumbs.forEach((t, i) => t.addEventListener('click', () => { goTo(i); markThumb(i); }));
    let current = 0;
    slides?.addEventListener('scroll', () => {
      const i = Math.round(slides.scrollLeft / slides.clientWidth);
      if (i !== current) { current = i; markThumb(i); }
    }, { passive: true });
    slides?.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(Math.min(current + 1, slideEls.length - 1)); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(Math.max(current - 1, 0)); }
    });

    /* lightbox */
    const lb = $('[data-lightbox]');
    const lbImg = $('[data-lightbox-img]');
    const showLb = (i) => {
      if (!media[i]) return;
      current = i;
      lbImg.src = media[i].src;
      lbImg.alt = media[i].alt || '';
    };
    $('[data-zoom-open]', section)?.addEventListener('click', () => {
      if (!lb?.showModal) return;
      showLb(current); lb.showModal();
    });
    slideEls.forEach((s, i) => s.addEventListener('click', () => { if (lb?.showModal && window.matchMedia('(hover: hover)').matches) { showLb(i); lb.showModal(); } }));
    $('[data-lightbox-close]')?.addEventListener('click', () => lb.close());
    $('[data-lightbox-prev]')?.addEventListener('click', () => showLb((current - 1 + media.length) % media.length));
    $('[data-lightbox-next]')?.addEventListener('click', () => showLb((current + 1) % media.length));
    lb?.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') showLb((current + 1) % media.length);
      if (e.key === 'ArrowLeft') showLb((current - 1 + media.length) % media.length);
    });
    lb?.addEventListener('close', () => { goTo(current, false); markThumb(current); });

    /* variants */
    const selected = () => $$('fieldset[data-option-index]', section).map((fs) => $('input:checked', fs)?.value);
    const findVariant = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));
    const showVariantImage = (variant) => {
      let idx = -1;
      if (variant.featured_media) idx = media.findIndex((m) => m.id === variant.featured_media.id);
      if (idx < 0) {
        // Pick the first photo whose alt text mentions this colour before any other colour,
        // e.g. "Stack ... with Slate Blue on top, Hot Pink and White below" belongs to Slate Blue.
        const names = [...new Set(variants.map((x) => (x.option1 || '').toLowerCase()))].filter(Boolean);
        const name = (variant.option1 || '').toLowerCase();
        idx = media.findIndex((m) => {
          const alt = (m.alt || '').toLowerCase();
          if (alt.includes('size diagram')) return false;
          let first = null, pos = Infinity;
          names.forEach((n) => { const p = alt.indexOf(n); if (p >= 0 && p < pos) { pos = p; first = n; } });
          return first === name;
        });
      }
      if (idx >= 0) { goTo(idx); markThumb(idx); current = idx; }
    };
    const update = () => {
      const opts = selected();
      $$('fieldset[data-option-index]', section).forEach((fs, i) => { const l = $('[data-option-label]', fs); if (l) l.textContent = opts[i]; });
      const v = findVariant(opts);
      if (!v) {
        addBtn.disabled = true; addLabel.textContent = 'Unavailable';
        return;
      }
      idInput.value = v.id;
      $$('[data-price]').forEach((p) => { p.textContent = money(v.price); });
      const sku = $('[data-sku]', section); if (sku) sku.textContent = v.sku || '';
      addBtn.disabled = !v.available;
      addLabel.textContent = v.available ? 'Add to cart' : 'Sold out';
      const sAdd = $('[data-sticky-add]'); if (sAdd) sAdd.disabled = !v.available;
      if (stock) {
        if (!v.available) stock.innerHTML = '<p class="stock stock--out">Sold out. <a href="/pages/contact">Ask us about restock</a></p>';
        else if (v.inventory_management === 'shopify' && typeof v.inventory_quantity === 'number' && v.inventory_quantity <= 3) stock.innerHTML = `<p class="stock stock--low">${v.inventory_quantity} in stock</p>`;
        else stock.innerHTML = '<p class="stock">In stock, ready to ship</p>';
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
      showVariantImage(v);
    };
    $$('fieldset[data-option-index] input', section).forEach((i) => i.addEventListener('change', update));

    /* quantity */
    const qty = $('[data-qty] input', section);
    $('[data-qty-minus]', section)?.addEventListener('click', () => { qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) - 1); });
    $('[data-qty-plus]', section)?.addEventListener('click', () => {
      const max = parseInt(qty.max, 10);
      const next = (parseInt(qty.value, 10) || 1) + 1;
      qty.value = max ? Math.min(max, next) : next;
    });

    form?.addEventListener('submit', (e) => { e.preventDefault(); addToCart(form, addBtn); });

    /* sticky bar on mobile */
    if (sticky && addBtn && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        $('[data-sticky-add]', sticky).tabIndex = show ? 0 : -1;
      }).observe(addBtn);
      $('[data-sticky-add]', sticky)?.addEventListener('click', () => addToCart(form, addBtn));
    }
  });

  /* ---------- size guide ---------- */
  const canvas = $('[data-size-canvas]');
  if (canvas) {
    const btns = $$('[data-size-btn]');
    const towels = $$('.towel', canvas);
    const set = (size) => {
      btns.forEach((b) => b.setAttribute('aria-pressed', b.dataset.sizeBtn === size ? 'true' : 'false'));
      towels.forEach((t) => t.classList.toggle('is-active', t.dataset.size === size));
      if (size) canvas.setAttribute('data-active', size); else canvas.removeAttribute('data-active');
    };
    btns.forEach((b) => {
      b.addEventListener('click', () => set(b.getAttribute('aria-pressed') === 'true' ? null : b.dataset.sizeBtn));
      b.addEventListener('mouseenter', () => { if (!btns.some((x) => x.getAttribute('aria-pressed') === 'true')) { towels.forEach((t) => t.classList.toggle('is-active', t.dataset.size === b.dataset.sizeBtn)); canvas.setAttribute('data-active', b.dataset.sizeBtn); } });
      b.addEventListener('mouseleave', () => { if (!btns.some((x) => x.getAttribute('aria-pressed') === 'true')) set(null); });
    });
  }

  /* ---------- collection filters ---------- */
  $$('[data-filter-form]').forEach((f) => {
    $$('[data-auto-submit]', f).forEach((i) => i.addEventListener('change', () => f.submit()));
  });
  document.addEventListener('click', (e) => {
    $$('details.filter[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });

  /* ---------- quote form ---------- */
  const quote = $('[data-quote-form]');
  if (quote) {
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('product');
    if (handle) {
      const row = $(`[data-quote-product="${CSS.escape(handle)}"]`, quote);
      const input = row && $('[data-quote-qty]', row);
      if (input) {
        row.style.borderColor = 'var(--ink)';
        input.value = quote.dataset.moq || '';
        setTimeout(() => { row.scrollIntoView({ block: 'center', behavior: 'smooth' }); input.focus({ preventScroll: true }); }, 300);
      }
    }
    quote.addEventListener('submit', (e) => {
      const qtys = $$('[data-quote-qty]', quote).map((i) => parseInt(i.value, 10) || 0);
      const notes = ($('textarea', quote)?.value || '').trim();
      if (!qtys.some((q) => q > 0) && notes.length < 5) {
        e.preventDefault();
        toast('Please enter a quantity for at least one towel, or describe what you need.');
        $('[data-quote-qty]', quote)?.focus();
      }
    });
  }

  /* ---------- focus result notices after form posts ---------- */
  const notice = $('[data-focus-on-load]');
  if (notice && /contact_posted|form_type|customer_posted/.test(window.location.search + window.location.hash)) {
    notice.scrollIntoView({ block: 'center' });
    notice.focus({ preventScroll: true });
  }

  /* ---------- cookie consent (Shopify Customer Privacy API) ---------- */
  const banner = $('[data-cookie]');
  const withPrivacy = (cb) => {
    if (window.Shopify?.customerPrivacy) return cb(window.Shopify.customerPrivacy);
    if (!window.Shopify?.loadFeatures) return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
      if (!err && window.Shopify.customerPrivacy) cb(window.Shopify.customerPrivacy);
    });
  };
  const DECIDED = 'kh_cookie_decided';
  const store = { get() { try { return localStorage.getItem(DECIDED); } catch (e) { return null; } }, set() { try { localStorage.setItem(DECIDED, '1'); } catch (e) { /* private mode */ } } };
  const showBanner = (prefs = false) => {
    if (!banner) return;
    banner.hidden = false;
    $('[data-cookie-main]', banner).hidden = prefs;
    $('[data-cookie-form]', banner).hidden = !prefs;
    if (prefs) {
      withPrivacy((cp) => {
        const c = cp.currentVisitorConsent?.() || {};
        const f = $('[data-cookie-form]', banner);
        f.preferences.checked = c.preferences === 'yes';
        f.analytics.checked = c.analytics === 'yes';
        f.marketing.checked = c.marketing === 'yes';
      });
    }
    requestAnimationFrame(() => focusables(banner)[0]?.focus({ preventScroll: true }));
  };
  const save = (consent) => {
    store.set();
    banner && (banner.hidden = true);
    withPrivacy((cp) => cp.setTrackingConsent({ ...consent, sale_of_data: consent.marketing }, (res) => {
      toast(res && res.error ? 'Sorry, we couldn’t save that. Please try again.' : 'Cookie preferences saved');
    }));
  };
  if (banner) {
    $('[data-cookie-accept]', banner).addEventListener('click', () => save({ analytics: true, marketing: true, preferences: true }));
    $('[data-cookie-reject]', banner).addEventListener('click', () => save({ analytics: false, marketing: false, preferences: false }));
    $('[data-cookie-prefs]', banner).addEventListener('click', () => showBanner(true));
    $('[data-cookie-back]', banner).addEventListener('click', () => showBanner(false));
    $('[data-cookie-form]', banner).addEventListener('submit', (e) => {
      e.preventDefault();
      const f = e.currentTarget;
      save({ analytics: f.analytics.checked, marketing: f.marketing.checked, preferences: f.preferences.checked });
    });
    // Defer so Shopify's own banner (if enabled) can register first.
    window.addEventListener('load', () => setTimeout(() => {
      if (window.privacyBanner) return; // Shopify's built-in banner is active: let it handle consent.
      if (store.get()) return;
      withPrivacy((cp) => {
        const c = cp.currentVisitorConsent?.() || {};
        const undecided = !c.analytics && !c.marketing;
        if (undecided || !store.get()) showBanner(false);
      });
    }, 600));
  }
  $$('[data-cookie-open]').forEach((b) => b.addEventListener('click', () => {
    if (window.privacyBanner?.showPreferences) { window.privacyBanner.showPreferences(); return; }
    if (banner) showBanner(true);
  }));

  /* ---------- reveal on scroll (fallback when GSAP motion is off) ---------- */
  const reveal = $$('[data-reveal]');
  if (document.documentElement.classList.contains('motion') && window.gsap) {
    // kh-motion.js handles reveals. If it fails to start, show everything after 3s.
    setTimeout(() => {
      if (!document.documentElement.classList.contains('motion-ready')) {
        document.documentElement.classList.add('motion-ready');
        reveal.forEach((el) => el.classList.add('is-in'));
      }
    }, 3000);
  } else if ('IntersectionObserver' in window && reveal.length) {
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
    }), { rootMargin: '0px 0px -8% 0px' });
    reveal.forEach((el) => io.observe(el));
  } else reveal.forEach((el) => el.classList.add('is-in'));
})();
